/**
 * Centralized Keyboard Event Management System
 * Handles global keyboard shortcuts with proper priority and conflict resolution
 */

export type KeyboardHandler = {
  id: string;
  key: string;
  modifiers?: {
    ctrl?: boolean;
    cmd?: boolean;
    shift?: boolean;
    alt?: boolean;
  };
  handler: (event: KeyboardEvent) => void | boolean;
  priority: number; // Higher number = higher priority
  preventDefault?: boolean;
  stopPropagation?: boolean;
  allowInInput?: boolean; // Allow this shortcut even when input is focused
  description?: string;
};

export type ModalStackEntry = {
  id: string;
  /** Return false to leave Escape available to the focused control. */
  onEscape?: () => void | boolean;
  priority: number;
  timestamp: number;
};

class KeyboardEventManager {
  private static instance: KeyboardEventManager;
  private handlers: Map<string, KeyboardHandler[]> = new Map();
  private modalStack: ModalStackEntry[] = [];
  private isListening = false;
  private activeHandlers = new Map<string, number>();
  private handledEvents = new WeakSet<KeyboardEvent>();

  private constructor() {
    this.startListening();
  }

  static getInstance(): KeyboardEventManager {
    if (!KeyboardEventManager.instance) {
      KeyboardEventManager.instance = new KeyboardEventManager();
    }
    return KeyboardEventManager.instance;
  }

  /**
   * Register a keyboard shortcut handler
   */
  register(handler: KeyboardHandler): () => void {
    const key = this.generateKey(handler.key, handler.modifiers);
    
    // if (process.env.NODE_ENV === 'development') {
    //   log.debug('KeyboardEventManager', `[KeyboardManager] Registering handler: ${handler.id} for key: ${key}`);
    // }
    
    if (!this.handlers.has(key)) {
      this.handlers.set(key, []);
    }
    
    const handlers = this.handlers.get(key)!;
    handlers.push(handler);
    
    // Sort by priority (descending)
    handlers.sort((a, b) => b.priority - a.priority);
    
    // Return unregister function
    return () => {
      const currentHandlers = this.handlers.get(key);
      if (currentHandlers) {
        const index = currentHandlers.indexOf(handler);
        if (index > -1) {
          currentHandlers.splice(index, 1);
          this.activeHandlers.delete(`${handler.id}-${key}`);
        }
        if (currentHandlers.length === 0) {
          this.handlers.delete(key);
        }
      }
    };
  }

  /**
   * Push a modal to the ESC handling stack
   */
  pushModal(entry: Omit<ModalStackEntry, 'timestamp'>): () => void {
    const modalEntry: ModalStackEntry = {
      ...entry,
      timestamp: Date.now()
    };
    
    // Stable sorting keeps the newest registration first within each priority,
    // even when wall-clock readings tie or move backwards.
    this.modalStack.unshift(modalEntry);
    this.modalStack.sort((a, b) => b.priority - a.priority);
    
    // Return function to remove from stack
    return () => {
      const index = this.modalStack.indexOf(modalEntry);
      if (index > -1) {
        this.modalStack.splice(index, 1);
      }
    };
  }

  /**
   * Get the current top modal
   */
  getTopModal(): ModalStackEntry | null {
    return this.modalStack[0] || null;
  }

  /**
   * Check if a specific modal is on top
   */
  isModalOnTop(modalId: string): boolean {
    const topModal = this.getTopModal();
    return topModal?.id === modalId;
  }

  /**
   * Generate a unique key for handler lookup
   */
  private generateKey(key: string, modifiers?: KeyboardHandler['modifiers']): string {
    const parts = [key.toLowerCase()];
    
    if (modifiers?.ctrl) parts.push('ctrl');
    if (modifiers?.cmd) parts.push('cmd');
    if (modifiers?.shift) parts.push('shift');
    if (modifiers?.alt) parts.push('alt');
    
    return parts.sort().join('+');
  }

  /**
   * Check if currently focused element is an input
   */
  private isInputFocused(): boolean {
    const activeElement = document.activeElement;
    if (!activeElement) return false;
    
    const tagName = activeElement.tagName;
    const isInput = tagName === 'INPUT' || tagName === 'TEXTAREA';
    const isContentEditable = activeElement.getAttribute('contenteditable') === 'true';
    
    return isInput || isContentEditable;
  }

  /**
   * Main keyboard event handler
   */
  private handleKeyDown = (event: KeyboardEvent) => {
    if (event.isComposing || event.keyCode === 229) return;
    if (this.handledEvents.has(event)) return;
    this.handledEvents.add(event);

    // Special handling for ESC key
    if (event.key === 'Escape') {
      this.handleEscapeKey(event);
      return;
    }
    
    // For cross-platform compatibility, check both ctrl and cmd
    const modifiers: KeyboardHandler['modifiers'] = {
      shift: event.shiftKey,
      alt: event.altKey
    };
    
    // Try to find handlers for both Ctrl and Cmd variants
    let handlers: KeyboardHandler[] | undefined;
    let matchedKey: string | undefined;
    
    if (event.ctrlKey) {
      modifiers.ctrl = true;
      const ctrlKey = this.generateKey(event.key, modifiers);
      // if (process.env.NODE_ENV === 'development') {
      //   log.debug('KeyboardEventManager', `[KeyboardManager] Looking for Ctrl key: ${ctrlKey}`);
      // }
      handlers = this.handlers.get(ctrlKey);
      if (handlers) matchedKey = ctrlKey;
    }
    
    if (!handlers && event.metaKey) {
      delete modifiers.ctrl;
      modifiers.cmd = true;
      const cmdKey = this.generateKey(event.key, modifiers);
      // if (process.env.NODE_ENV === 'development') {
      //   log.debug('KeyboardEventManager', `[KeyboardManager] Looking for Cmd key: ${cmdKey}`);
      // }
      handlers = this.handlers.get(cmdKey);
      if (handlers) matchedKey = cmdKey;
    }
    
    if (!handlers || handlers.length === 0) {
      // if (process.env.NODE_ENV === 'development' && (event.ctrlKey || event.metaKey)) {
      //   log.debug('KeyboardEventManager', `[KeyboardManager] No handlers found for key: ${event.key}`);
      // }
      return;
    }
    
    // Check if input is focused
    const isInputFocused = this.isInputFocused();
    let selectedPriority: number | undefined;
    
    // Process handlers in priority order
    for (const handler of handlers) {
      // Skip if input is focused and handler doesn't allow it
      if (isInputFocused && !handler.allowInInput) continue;
      if (selectedPriority === undefined) selectedPriority = handler.priority;
      if (handler.priority !== selectedPriority) break;
      
      if (handler.preventDefault) {
        event.preventDefault();
      }
      if (handler.stopPropagation) {
        event.stopPropagation();
      }
      const handlerKey = `${handler.id}-${matchedKey}`;
      const lastHandledAt = this.activeHandlers.get(handlerKey);
      if (event.repeat && lastHandledAt !== undefined && Date.now() - lastHandledAt < 100) {
        if (handler.stopPropagation || event.cancelBubble) return;
        continue;
      }

      this.activeHandlers.set(handlerKey, Date.now());
      const result = handler.handler(event);
      if (result === false || handler.stopPropagation || event.cancelBubble) break;
    }
  };

  /**
   * Handle ESC key with modal stack
   */
  private handleEscapeKey(event: KeyboardEvent) {
    // Don't handle ESC in inputs unless there's a modal
    if (this.isInputFocused() && this.modalStack.length === 0) {
      return;
    }
    
    // Get the top modal
    const topModal = this.getTopModal();
    if (topModal && topModal.onEscape) {
      // A focused control can explicitly retain Escape (for example, to
      // discard an in-progress text draft) without weakening normal modal
      // Escape behavior everywhere else.
      if (topModal.onEscape() === false) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    
    // If no modal, check for registered ESC handlers
    const handlers = this.handlers.get('escape');
    if (handlers && handlers.length > 0) {
      let selectedPriority: number | undefined;
      for (const handler of handlers) {
        if (this.isInputFocused() && !handler.allowInInput) continue;
        if (selectedPriority === undefined) selectedPriority = handler.priority;
        if (handler.priority !== selectedPriority) break;
        
        if (handler.preventDefault) {
          event.preventDefault();
        }
        if (handler.stopPropagation) {
          event.stopPropagation();
        }
        
        const result = handler.handler(event);
        if (result === false || handler.stopPropagation || event.cancelBubble) break;
      }
    }
  }

  /**
   * Start listening to keyboard events
   */
  private startListening() {
    if (this.isListening) return;
    
    document.addEventListener('keydown', this.handleKeyDown, true);
    this.isListening = true;
  }

  /**
   * Stop listening to keyboard events
   */
  destroy() {
    if (!this.isListening) return;
    
    document.removeEventListener('keydown', this.handleKeyDown, true);
    this.handlers.clear();
    this.modalStack = [];
    this.activeHandlers.clear();
    this.handledEvents = new WeakSet();
    this.isListening = false;
  }

  /**
   * Debug: Get current state
   */
  getDebugInfo() {
    return {
      handlers: Array.from(this.handlers.entries()).map(([key, handlers]) => ({
        key,
        count: handlers.length,
        handlers: handlers.map(h => ({
          id: h.id,
          priority: h.priority,
          description: h.description
        }))
      })),
      modalStack: this.modalStack.map(m => ({
        id: m.id,
        priority: m.priority
      })),
      activeHandlers: Array.from(this.activeHandlers)
        .filter(([, timestamp]) => Date.now() - timestamp < 100)
        .map(([key]) => key)
    };
  }
}

// Export singleton instance
export const keyboardManager = KeyboardEventManager.getInstance();

// Helper hook for React components
export function useKeyboardShortcut(
  key: string,
  handler: (event: KeyboardEvent) => void,
  options: {
    modifiers?: KeyboardHandler['modifiers'];
    priority?: number;
    preventDefault?: boolean;
    stopPropagation?: boolean;
    allowInInput?: boolean;
    description?: string;
    enabled?: boolean;
  } = {}
) {
  const { enabled = true, ...handlerOptions } = options;
  
  React.useEffect(() => {
    if (!enabled) return;
    
    const unregister = keyboardManager.register({
      id: `hook-${Math.random().toString(36).substr(2, 9)}`,
      key,
      handler,
      priority: handlerOptions.priority || 0,
      ...handlerOptions
    });
    
    return unregister;
  }, [key, handler, enabled, handlerOptions]);
}

// Helper hook for modal ESC handling
export function useModalEscape(
  modalId: string,
  onEscape: () => void | boolean,
  options: {
    priority?: number;
    enabled?: boolean;
  } = {}
) {
  const { priority = 100, enabled = true } = options;
  const onEscapeRef = React.useRef(onEscape);
  React.useLayoutEffect(() => {
    onEscapeRef.current = onEscape;
  }, [onEscape]);
  
  React.useEffect(() => {
    if (!enabled) return;
    
    const unregister = keyboardManager.pushModal({
      id: modalId,
      onEscape: () => onEscapeRef.current(),
      priority
    });
    
    return unregister;
  }, [modalId, priority, enabled]);
  
  // Return helper to check if this modal is on top
  return {
    isOnTop: () => keyboardManager.isModalOnTop(modalId)
  };
}

// Re-export React for the helper hooks
import * as React from 'react';
